/**
 * Root route — Surface Layer (/01)
 *
 * Owns the global session lifecycle, the auth guard, the WebKit
 * ROUTE_CHANGED dispatch, and the conditional SurfaceShell. Product
 * (TenantPlane/UserHub and ControlPlane/DevHub) routes render inside the
 * WorkspaceShell; the public sign-in / onboarding routes render bare.
 */

import { createRootRoute, Outlet, redirect, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { WorkspaceShell } from '@shell/WorkspaceShell';
import { SurfaceErrorBoundary } from '@shell/SurfaceErrorBoundary';
import { useSurfaceSession } from '@auth/useSurfaceSession';
import { useAuthStore } from '@auth/auth-store';
import { isFragileSession, getSecurityGate } from '@auth/session-strength';
import {
  isPublicRoute,
  isNoShellRoute,
  isPlaneAccessBlocked,
  LOGIN_ROUTE,
  DASHBOARD_ROUTE,
} from '@lib/navigation/routePolicy';
import { planeForPath } from '@lib/navigation/hubPlane';
import { useResolvedPlane } from '@lib/navigation/useActivePlane';
import { emit as emitBridgeEvent } from '@lib/webkit/messageHandler';
import { track } from '@lib/telemetry';

export const Route = createRootRoute({
  beforeLoad: async ({ location }) => {
    const path = location.pathname;
    const identity = useAuthStore.getState().identity;
    if (!identity && !isPublicRoute(path)) {
      throw redirect({ to: LOGIN_ROUTE });
    }
    if (!identity || isPublicRoute(path)) return;
    // Auth Flow v2 routing (enforcement is the BFF/RP `amr` gate):
    // 1. Fragile (password-only) sessions only see the step-up interstitial.
    if (isFragileSession() && path !== '/step-up') {
      throw redirect({ to: '/step-up' });
    }
    if (path === '/step-up' || path === '/onboarding') return;
    // 2. Setup incomplete (no password/passkey) or magic-link arrival →
    //    security ceremony in the onboarding wizard before the app.
    const gate = await getSecurityGate();
    if (gate && (!gate.setupComplete || gate.rotationPending)) {
      throw redirect({ to: '/onboarding' });
    }
    // 3. Plane boundary: a member deep-linking the operator plane (or an
    //    operator-less identity hitting it) is sent to their own dashboard —
    //    never a cross-hub leak. The dashboard renders the user's own plane.
    if (isPlaneAccessBlocked(path, identity.roles)) {
      throw redirect({ to: DASHBOARD_ROUTE });
    }
  },
  component: RootComponent,
});

function RootComponent() {
  // Start the global session lifecycle (restore + subscribe) once.
  useSurfaceSession();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const resolvedPlane = useResolvedPlane();

  useEffect(() => {
    // Tell the WebKit host which plane is active (plane-specific routes are
    // explicit; otherwise the identity's resolved plane).
    const plane = (planeForPath(pathname) ?? resolvedPlane) === 'devhub' ? 'control' : 'tenant';
    emitBridgeEvent({
      type: 'ROUTE_CHANGED',
      payload: { from: 'unknown', to: pathname, plane },
    });
    track('route.view', { to: pathname });
  }, [pathname, resolvedPlane]);

  if (isNoShellRoute(pathname)) {
    return (
      <SurfaceErrorBoundary>
        <Outlet />
      </SurfaceErrorBoundary>
    );
  }

  return (
    <WorkspaceShell>
      <SurfaceErrorBoundary>
        <Outlet />
      </SurfaceErrorBoundary>
    </WorkspaceShell>
  );
}
